import {chain, concat} from 'lodash';
import React, {Fragment, ReactNode} from 'react';
import styled from 'styled-components';

import {renderedLanguagesMap} from '../../../libs/knowledge-base/shared/helpers/knowledgeBaseMultiLangHelpers';
import {addQueryToPath} from '../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isEnumType} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {areUnorderedEqual} from '../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {CancelError, CancelToken} from '../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {
  CompanyFeaturesEnum,
  CompanyModel,
} from '../../../libs/shared/core/src/models/api/company/companyModel';
import {DecisionTreeModel} from '../../../libs/shared/core/src/models/api/decisionTreeModel';
import {KnowledgeBaseSupportedLanguagesTypesEnum} from '../../../libs/shared/core/src/models/api/knowledgeBaseModel';
import {isTeammateModel, TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {TeamModel} from '../../../libs/shared/core/src/models/api/teamModel';
import {
  computeIsGlobalNamespace,
  globalNamespace,
  isTeamNamespace,
} from '../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {buildTeammateDisplayName} from '../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {fetchApiAsync} from '../../../libs/shared/core/src/services/http/http';
import {HttpError} from '../../../libs/shared/core/src/services/http/httpError';
import {ResourceMap} from '../../../libs/shared/core/src/store/commonAppTypes';
import {TeamAvatar} from '../../../libs/shared/ui/src/components/avatar/teamAvatar';
import {TeammateAvatar} from '../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {Icon, IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {ColorDot} from '../../../libs/shared/ui/src/components/utility/colorDot';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis, flexCenter} from '../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {AccountAvatar} from '../../components/common/avatars/accountAvatar';
import {renderTagIcon} from '../../components/common/dropdowns/dropdownItemTagIcon';
import {DropdownFetcher} from '../../components/common/dropdowns/source/asyncDropdownSource';
import {getChannelIconName} from '../../helpers/channelViewHelpers';
import {inboxHasColor} from '../../helpers/modelHelpers';
import {buildTagSearchUrl, SearchTagStatusesEnum} from '../../helpers/models/tagHelpers';
import {AccountModel} from '../../models/api/accountModel';
import {AnalyticsChannelModel, AnalyticsInboxModel} from '../../models/api/analyticsFilterResourcesModel';
import {
  AnalyticsFilterTypesEnum,
  AnalyticsInboxScopeFiltersEnum,
} from '../../models/api/analyticsSavedViewModel';
import {ApiAnalyticsSearchChannels} from '../../models/api/analyticsSearchChannelsModel';
import {ApiAnalyticsSearchInboxes} from '../../models/api/analyticsSearchInboxesModel';
import {CircleModel, isCircleModel} from '../../models/api/circleModel';
import {ApiSearchTags, SearchTagModel} from '../../models/api/searchTagModel';
import {TagBehaviorsEnum, TagModel} from '../../models/api/tagModel';
import {makeAnalyticsApiPath} from '../../store/panels/analytics/analyticsPanelHelpers';
import {FilterContent} from './filterContent';

/*
 * Constants.
 */

// Map each feature to the days allowed in for the time range (can be unlimited).
export const ANALYTICS_TIME_PERIODS = {
  [CompanyFeaturesEnum.MEDIUM_ANALYTICS_TIME_RANGE]: 217, // 7 months (trial, growth).
  [CompanyFeaturesEnum.LARGE_ANALYTICS_TIME_RANGE]: 775, // 25 months (scale, premium).
  [CompanyFeaturesEnum.UNLIMITED_ANALYTICS_TIME_RANGE]: -1, // Unlimited (old plans, exceptions).
};

/*
 * Style.
 */

const StyledColorDot = styled(ColorDot)`
  margin: 0 7px;
  display: inline-block;
`;

const StyledInlineIcon = styled(Icon)`
  display: inline-block;
  margin: 0 4px;
  vertical-align: middle;
`;

const StyledTagWrapper = styled.div`
  ${flexCenter};
`;

const StyledAccountAvatar = styled(AccountAvatar)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  bottom: 1px;
  margin-right: 4px;
`;

const StyledTagTitle = styled.div`
  ${ellipsis()};
`;

const StyledTagIcon = styled.div`
  margin-right: 4px;
`;

const StyledCircleIcon = styled(Icon)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
`;

const StyledTeammateAvatar = styled(TeammateAvatar)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
`;

const StyledTeamAvatar = styled(TeamAvatar)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
`;

const StyledVerticalAlign = styled.span`
  vertical-align: middle;
`;

/*
 * Constants.
 */

const accountIconSize = 16;

export interface FilterRenderer<T> {
  renderItem: RendererOf<T>;
  renderSpecialValue?: RendererOf<AnalyticsInboxScopeFiltersEnum>;
}

const inboxScopeFilterRenderer: Record<AnalyticsInboxScopeFiltersEnum, ReactNode> = {
  [AnalyticsInboxScopeFiltersEnum.ALL_INBOXES]: <Trans id="analytics-filter-all-inboxes">All inboxes</Trans>,
  [AnalyticsInboxScopeFiltersEnum.ALL_SHARED_INBOXES]: (
    <Trans id="analytics-filter-all-shared-inboxes">All shared inboxes</Trans>
  ),
  [AnalyticsInboxScopeFiltersEnum.ALL_PRIVATE_INBOXES]: (
    <Trans id="analytics-filter-all-private-values">All individual inboxes</Trans>
  ),
};

export const filterRenderers: Record<AnalyticsFilterTypesEnum, FilterRenderer<any>> = {
  [AnalyticsFilterTypesEnum.ACCOUNTS]: {
    renderItem: (account: AccountModel) => (
      <Fragment key={account.id}>
        <StyledAccountAvatar account={account} size={accountIconSize} avatarIconSize={accountIconSize} />
        {account.name}
      </Fragment>
    ),
  },
  [AnalyticsFilterTypesEnum.INBOXES]: {
    renderItem: (inbox: AnalyticsInboxModel) => (
      <UserThemeConsumer>
        {(theme) => (
          <Fragment key={inbox.id}>
            <StyledColorDot color={inboxHasColor(inbox) ? inbox.color : theme.greys.shade60} size={8} />
            {inbox.name}
          </Fragment>
        )}
      </UserThemeConsumer>
    ),
    renderSpecialValue: (value: AnalyticsInboxScopeFiltersEnum) => (
      <Fragment key={value}>{inboxScopeFilterRenderer[value]}</Fragment>
    ),
  },
  [AnalyticsFilterTypesEnum.FULL_TAGS]: {
    renderItem: (tag: TagModel) => (
      <StyledTagWrapper key={tag.name}>
        <StyledTagIcon>{renderTagIcon(tag)}</StyledTagIcon>
        <StyledTagTitle>{tag.name}</StyledTagTitle>
      </StyledTagWrapper>
    ),
  },
  [AnalyticsFilterTypesEnum.TAGS]: {
    renderItem: (tagName: string) => <Fragment key={tagName}>{tagName}</Fragment>,
  },
  [AnalyticsFilterTypesEnum.EXCLUDE_TAGS]: {
    renderItem: (tagName: string) => <Fragment key={tagName}>{tagName}</Fragment>,
  },
  [AnalyticsFilterTypesEnum.WITHOUT_TAGS]: {
    renderItem: () => null,
  },
  [AnalyticsFilterTypesEnum.CHANNELS]: {
    renderItem: (channel: AnalyticsChannelModel) => (
      <UserThemeConsumer>
        {(theme) => (
          <Fragment key={channel.id}>
            <StyledInlineIcon name={getChannelIconName(channel.contactSource)} color={theme.greys.shade50} />
            {channel.displayName}
          </Fragment>
        )}
      </UserThemeConsumer>
    ),
  },
  [AnalyticsFilterTypesEnum.TEAMMATES]: {
    renderItem: (resource: TeammateModel | CircleModel) => (
      <UserThemeConsumer>
        {(theme) => (
          <Fragment key={resource.id}>
            {isTeammateModel(resource) && (
              <>
                <StyledTeammateAvatar teammate={resource} size={VisualSizesEnum.SMALL} />
                <StyledVerticalAlign>
                  &nbsp;
                  {buildTeammateDisplayName(resource)}
                </StyledVerticalAlign>
              </>
            )}

            {isCircleModel(resource) && (
              <>
                <StyledCircleIcon name="groupSmall" color={theme.greys.shade60} />
                <StyledVerticalAlign>
                  &nbsp;
                  {resource.name}
                </StyledVerticalAlign>
              </>
            )}
          </Fragment>
        )}
      </UserThemeConsumer>
    ),
  },
  [AnalyticsFilterTypesEnum.TEAMS]: {
    renderItem: (team: TeamModel) => (
      <Fragment key={team.id}>
        <StyledTeamAvatar team={team} size={VisualSizesEnum.SMALL} /> {team.name}
      </Fragment>
    ),
  },
  [AnalyticsFilterTypesEnum.CHATBOTS]: {
    renderItem: (chatbot: DecisionTreeModel) => <Fragment key={chatbot.id}>{chatbot.name}</Fragment>,
  },
  [AnalyticsFilterTypesEnum.KNOWLEDGE_BASES]: {
    renderItem: (knowledgeBase: DecisionTreeModel) => (
      <Fragment key={knowledgeBase.id}>{knowledgeBase.name}</Fragment>
    ),
  },
  [AnalyticsFilterTypesEnum.LOCALE]: {
    renderItem: (locale: KnowledgeBaseSupportedLanguagesTypesEnum) => (
      <Fragment key={locale}>{renderedLanguagesMap[locale]}</Fragment>
    ),
  },
};

/*
 * Helpers.
 */

type RenderFilterIdsType<T> = {
  ids: ReadonlyArray<keyof ResourceMap<T>> | undefined;
  itemsMap: ResourceMap<T>;
  type: AnalyticsFilterTypesEnum;
  iconName?: IconName;
};

export function renderFilterIds<T>({ids = [], itemsMap, type, iconName}: RenderFilterIdsType<T>) {
  const {renderItem, renderSpecialValue} = filterRenderers[type];

  // We can't just take the first "maxItemsCount" ids since we're not sure they are in itemsMap.
  // Let's find the first "maxItemsCount" items that we have data on in itemsMap.
  const itemsToShow: Array<{id: keyof ResourceMap<T>; node: ReactNode}> = [];

  for (const id of ids) {
    if (isEnumType(id, AnalyticsInboxScopeFiltersEnum) && renderSpecialValue) {
      itemsToShow.push({id, node: renderSpecialValue(id)});
    } else {
      const item = itemsMap[id];
      if (item) {
        itemsToShow.push({id, node: renderItem(item)});
      }
    }
  }

  const filteredValue = itemsToShow.reduce<Array<ReactNode>>((acc, {id, node}, index) => {
    // Wrapping into a span is necessary to allow the counting of elements
    const element = (
      <span key={id}>
        {node}
        {index < itemsToShow.length - 1 && ', '}
      </span>
    );
    acc.push(element);
    return acc;
  }, []);

  return (
    <FilterContent iconName={iconName} shouldShrink>
      {filteredValue}
    </FilterContent>
  );
}

type RenderFilterIdsV2Type<T> = {
  selectedIds: ReadonlyArray<keyof ResourceMap<T>>;
  itemsMap: ResourceMap<T>;
  renderItem: RendererOf<T>;
};

export function renderFilterIdsV2<T>({selectedIds, itemsMap, renderItem}: RenderFilterIdsV2Type<T>) {
  return selectedIds.reduce<Array<ReactNode>>((acc, id, index) => {
    const item = itemsMap[id];

    if (item) {
      // Wrapping into a span is necessary to allow the counting of elements
      const element = (
        <span key={id}>
          {renderItem(item)}
          {index < selectedIds.length - 1 && ', '}
        </span>
      );
      acc.push(element);
    }
    return acc;
  }, []);
}

export function makeFilterRows<T>(accessor: (item: T) => string) {
  return (items: ReadonlyArray<T>, query: string) => {
    // Shortcut for empty query.
    if (!query) {
      return items;
    }

    // Otherwise, filter with provided function.
    const lowerCaseQuery = query.toLowerCase();
    return chain(items)
      .compact() // Remove the fixed rows.
      .filter((i) => accessor(i).toLowerCase().includes(lowerCaseQuery))
      .value();
  };
}

/*
 * Tags fetcher.
 */

type TagsFetcherMaker = (
  company: CompanyModel,
  namespace: string,
  selectedTeamNamespaces: Set<string>,
) => DropdownFetcher<SearchTagModel>;

export const makeTagsFetcher: TagsFetcherMaker =
  (company: CompanyModel, namespace: string, selectedTeamNamespaces: Set<string>) =>
  async (query, tags, cancelToken) => {
    const behaviors = [TagBehaviorsEnum.CAN_ANALYTICS];

    // Exclude initial items to compute the offset.
    const tagCount = tags.filter((t) => t).length;

    const namespaces = computeTagsFetcherNamespaces(namespace, selectedTeamNamespaces);

    const url = buildTagSearchUrl(company, query, {
      namespaces,
      offset: tagCount,
      behaviors,
      status: SearchTagStatusesEnum.ALL,
      source: 'analytics-tags-filters',
    });

    const tagSearch = await fetchApiAsync(ApiSearchTags, url, {cancelToken});

    return {
      items: concat(tags, tagSearch.tags),
      canLoadMore: tagSearch.tags.length === tagSearch.limit,
    };
  };

function computeTagsFetcherNamespaces(
  namespace: string,
  selectedTeamNamespaces: Set<string>,
): ReadonlyArray<string> | undefined {
  if (computeIsGlobalNamespace(namespace)) {
    // Sending an empty array will return company tags + all team tags we have access to. Otherwise we manually query company tags + selected team tags.
    return selectedTeamNamespaces.size ? [globalNamespace, ...selectedTeamNamespaces] : [];
  }

  if (isTeamNamespace(namespace)) {
    return [globalNamespace, namespace];
  }

  // No FE restriction for an individual report, the BE will return all accessible tags.
  return undefined;
}

/*
 * Analytics Channels fetcher.
 */

const fetchChannelsLimit = 25;

interface ChannelsFilterPaginationData {
  offset?: number;
  teamNamespaces: ReadonlyArray<string>;
}

export function makeChannelsFetcher(
  company: CompanyModel,
  namespace: string,
  selectedTeamNamespaces: Set<string>,
  onChannelsFetched: (channels: ReadonlyArray<AnalyticsChannelModel>) => void,
): DropdownFetcher<AnalyticsChannelModel, ChannelsFilterPaginationData> {
  return async (
    query: string,
    channels: ReadonlyArray<AnalyticsChannelModel>,
    cancelToken: CancelToken,
    paginationData?: ChannelsFilterPaginationData,
  ) => {
    const selectedTeamNamespacesArray = [...selectedTeamNamespaces];
    const sameTeamNamespaces = areUnorderedEqual(
      paginationData?.teamNamespaces ?? [],
      selectedTeamNamespacesArray,
    );

    // reset offset if team namespaces changed
    const offset = sameTeamNamespaces ? paginationData?.offset ?? 0 : 0;

    const fetchChannelsUrl = makeSearchChannelsOrInboxesUrl(company, {
      namespace,
      query: query.length > 2 ? query : undefined,
      teamNamespaces: selectedTeamNamespacesArray,
      limit: fetchChannelsLimit,
      offset,
      resourceType: 'channels',
    });

    try {
      const response = await fetchApiAsync(ApiAnalyticsSearchChannels, fetchChannelsUrl, {cancelToken});
      cancelToken.throwIfRequested();

      const newOffset = (offset ?? 0) + response.limit;

      onChannelsFetched(response.channels);

      return {
        items: channels.concat(response.channels),
        canLoadMore: response.channels.length > 0 && response.limit < response.total,
        paginationData: {
          offset: newOffset,
          teamNamespaces: selectedTeamNamespacesArray,
        },
      };
    } catch (error) {
      if (!(error instanceof HttpError || error instanceof CancelError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

/*
 * Analytics Inboxes fetcher.
 */

const fetchInboxesLimit = 25;

interface InboxesFilterPaginationData {
  offset?: number;
  teamNamespaces: ReadonlyArray<string>;
}

export function makeInboxesFetcher(
  company: CompanyModel,
  namespace: string,
  selectedTeamNamespaces: Set<string>,
  onInboxedFetched: (inboxes: ReadonlyArray<AnalyticsInboxModel>) => void,
): DropdownFetcher<AnalyticsInboxModel, InboxesFilterPaginationData> {
  return async (
    query: string,
    inboxes: ReadonlyArray<AnalyticsInboxModel>,
    cancelToken: CancelToken,
    paginationData?: InboxesFilterPaginationData,
  ) => {
    const selectedTeamNamespacesArray = [...selectedTeamNamespaces];
    const sameTeamNamespaces = areUnorderedEqual(
      paginationData?.teamNamespaces ?? [],
      selectedTeamNamespacesArray,
    );

    const offset = sameTeamNamespaces ? paginationData?.offset ?? 0 : 0;

    const fetchInboxesUrl = makeSearchChannelsOrInboxesUrl(company, {
      namespace,
      query: query.length > 2 ? query : undefined,
      teamNamespaces: selectedTeamNamespacesArray,
      limit: fetchInboxesLimit,
      // reset offset if team namespaces changed
      offset,
      resourceType: 'inboxes',
    });

    try {
      const response = await fetchApiAsync(ApiAnalyticsSearchInboxes, fetchInboxesUrl, {cancelToken});
      cancelToken.throwIfRequested();

      const newOffset = (offset ?? 0) + response.limit;

      onInboxedFetched(response.inboxes);

      return {
        items: inboxes.concat(response.inboxes),
        canLoadMore: response.inboxes.length > 0 && response.limit < response.total,
        paginationData: {
          offset: newOffset,
          teamNamespaces: selectedTeamNamespacesArray,
        },
      };
    } catch (error) {
      if (!(error instanceof HttpError || error instanceof CancelError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

interface MakeSearchChannelsOrInboxesUrlOptions {
  namespace: string;
  limit: number;
  offset: number;
  teamNamespaces: ReadonlyArray<string>;
  query?: string;
  resourceType: 'channels' | 'inboxes';
}

function makeSearchChannelsOrInboxesUrl(
  company: CompanyModel,
  options: MakeSearchChannelsOrInboxesUrlOptions,
) {
  return addQueryToPath(
    `${makeAnalyticsApiPath(company.url, options.namespace)}/search_${
      options.resourceType
    }/${encodeURIComponent(options?.query ?? '')}`,
    {
      limit: options?.limit,
      offset: options?.offset,
      team_namespaces: options.teamNamespaces.length === 0 ? undefined : options.teamNamespaces,
    },
  );
}
